import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button, Tooltip, Checkbox } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaCheck, FaTimes, FaPenAlt, FaCar, FaUser, FaBell, FaCut } from 'react-icons/fa';
import { useAuth } from './AuthContext';
import Alerty from '../common/Alerty';
import PaymentTypeEditModal from './PaymentTypeEditModal';
const { rootUrl } = require('../../config/config');

const PaymentTypes = () => {
    const [activeTab, setActiveTab] = useState('Cars');
    const [carPaymentTypes, setCarPaymentTypes] = useState([]);
    const [driverPaymentTypes, setDriverPaymentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Modal states
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingPaymentType, setEditingPaymentType] = useState(null);
    const [editingType, setEditingType] = useState(''); // 'car' or 'driver'
    
    // Car payment type states for new entries
    const [carNewType, setCarNewType] = useState('');
    const [carNewShortDesc, setCarNewShortDesc] = useState('');
    const [carNewLongDesc, setCarNewLongDesc] = useState('');
    const [carNewSign, setCarNewSign] = useState('');
    const [carNewDriverPost, setCarNewDriverPost] = useState(false);
    const [carNewDriverPayType, setCarNewDriverPayType] = useState('');
    const [carNewSubType, setCarNewSubType] = useState('');
    const [carNewReversalType, setCarNewReversalType] = useState('');
    const [carNewDef, setCarNewDef] = useState(false);
    const [carNewNotify, setCarNewNotify] = useState(false);
    const [carNewCanSplit, setCarNewCanSplit] = useState(true);
    
    // Driver payment type states for new entries
    const [driverNewType, setDriverNewType] = useState('');
    const [driverNewShortDesc, setDriverNewShortDesc] = useState('');
    const [driverNewLongDesc, setDriverNewLongDesc] = useState('');
    const [driverNewSign, setDriverNewSign] = useState('');
    const [driverNewSubType, setDriverNewSubType] = useState('');
    const [driverNewReversalType, setDriverNewReversalType] = useState('');
    const [driverNewDef, setDriverNewDef] = useState(false);
    const [driverNewNotify, setDriverNewNotify] = useState(false);
    const [driverNewCarPost, setDriverNewCarPost] = useState(false);
    const [driverNewCarPostType, setDriverNewCarPostType] = useState('');
    
    const { user } = useAuth();

    // Car payment type functions
    const createCarPaymentType = async (e) => {
        e.preventDefault();
        if (!carNewType.trim() || !carNewShortDesc.trim() || !carNewLongDesc.trim() || !carNewSign.trim()) {
            setError('Type, Short Description, Long Description, and Sign are required.');
            return;
        }

        // Validate reversal_type if provided
        if (carNewReversalType.trim()) {
            const revType = carNewReversalType.trim().toUpperCase();
            const exists = carPaymentTypes.some(t => t.type === revType);
            if (!exists) {
                setError(`Invalid Reversal Type: '${revType}' does not exist in car payment types.`);
                return;
            }
        }

        try {
            await axios.post(`${rootUrl}/payments/car-types`, {
                type: carNewType.toUpperCase(),
                short_desc: carNewShortDesc.toUpperCase(),
                long_desc: carNewLongDesc,
                sign: carNewSign,
                driver_post: carNewDriverPost,
                driver_pay_type: carNewDriverPayType.toUpperCase(),
                sub_type: carNewSubType.toUpperCase(),
                reversal_type: carNewReversalType.toUpperCase(),
                def: carNewDef,
                notify: carNewNotify,
                can_split: carNewCanSplit,
                input_by: user.username
            });
            // clear inputs
            setCarNewType('');
            setCarNewShortDesc('');
            setCarNewLongDesc('');
            setCarNewSign('');
            setCarNewDriverPost(false);
            setCarNewDriverPayType('');
            setCarNewSubType('');
            setCarNewReversalType('');
            setCarNewDef(false);
            setCarNewNotify(false);
            setCarNewCanSplit(true);
            setError('');
            fetchCarPaymentTypes();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create new car payment type.');
        }
    };

    const fetchCarPaymentTypes = async () => {
        try {
            const response = await axios.get(`${rootUrl}/payments/car-types`);
            setCarPaymentTypes(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch car payment types.');
        }
    };

    const openEditModal = (paymentType, type) => {
        setEditingPaymentType(paymentType);
        setEditingType(type);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingPaymentType(null);
        setEditingType('');
    };

    const handleSaveEdit = async (type, data) => {
        try {
            const endpoint = editingType === 'car' ? 'car-types' : 'driver-types';
            await axios.put(`${rootUrl}/payments/${endpoint}/${type}`, data);
            
            // Refresh the appropriate data
            if (editingType === 'car') {
                await fetchCarPaymentTypes();
            } else {
                await fetchDriverPaymentTypes();
            }
            
            setError('');
        } catch (err) {
            setError(`Failed to update ${editingType} payment type.`);
        }
    };

    const deleteCarPaymentType = async (type) => {
        if (!window.confirm('Are you sure you want to delete this car payment type?')) return;
        try {
            await axios.delete(`${rootUrl}/payments/car-types/${type}`);
            fetchCarPaymentTypes();
        } catch (err) {
            setError('Failed to delete car payment type.');
        }
    };

    // Driver payment type functions
    const createDriverPaymentType = async (e) => {
        e.preventDefault();
        if (!driverNewType.trim() || !driverNewShortDesc.trim() || !driverNewLongDesc.trim() || !driverNewSign.trim()) {
            setError('Type, Short Description, Long Description, and Sign are required.');
            return;
        }

        // Validate reversal_type if provided
        if (driverNewReversalType.trim()) {
            const revType = driverNewReversalType.trim().toUpperCase();
            const exists = driverPaymentTypes.some(t => t.type === revType);
            if (!exists) {
                setError(`Invalid Reversal Type: '${revType}' does not exist in driver payment types.`);
                return;
            }
        }

        try {
            await axios.post(`${rootUrl}/payments/driver-types`, {
                type: driverNewType.toUpperCase(),
                short_desc: driverNewShortDesc.toUpperCase(),
                long_desc: driverNewLongDesc,
                sign: driverNewSign,
                sub_type: driverNewSubType.toUpperCase(),
                reversal_type: driverNewReversalType.toUpperCase(),
                def: driverNewDef,
                notify: driverNewNotify,
                car_post: driverNewCarPost,
                car_post_type: driverNewCarPostType.toUpperCase(),
                input_by: user.username
            });
            // clear inputs
            setDriverNewType('');
            setDriverNewShortDesc('');
            setDriverNewLongDesc('');
            setDriverNewSign('');
            setDriverNewSubType('');
            setDriverNewReversalType('');
            setDriverNewDef(false);
            setDriverNewNotify(false);
            setDriverNewCarPost(false);
            setDriverNewCarPostType('');
            setError('');
            fetchDriverPaymentTypes();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create new driver payment type.');
        }
    };

    const fetchDriverPaymentTypes = async () => {
        try {
            const response = await axios.get(`${rootUrl}/payments/driver-types`);
            setDriverPaymentTypes(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch driver payment types.');
        }
    };

    const deleteDriverPaymentType = async (type) => {
        if (!window.confirm('Are you sure you want to delete this driver payment type?')) return;
        try {
            await axios.delete(`${rootUrl}/payments/driver-types/${type}`);
            fetchDriverPaymentTypes();
        } catch (err) {
            setError('Failed to delete driver payment type.');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([fetchCarPaymentTypes(), fetchDriverPaymentTypes()]);
            setLoading(false);
        };
        fetchData();
    }, []);

    const renderCarPaymentTypes = () => (
        <>
            {/* Add New Car Payment Type Form */}
            <Card className="mb-6 shadow-lg border border-gray-100">
                <CardBody className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <FaPlus className="text-blue-600" />
                        </div>
                        <Typography variant="h6" color="blue-gray">
                            Add New Car Payment Type
                        </Typography>
                    </div>
                    <form onSubmit={createCarPaymentType}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <Input
                                type="text"
                                label="Type Code"
                                value={carNewType}
                                onChange={e => setCarNewType(e.target.value.slice(0, 10).toUpperCase())}
                                maxLength={10}
                                color="blue"
                            />
                            <Input
                                type="text"
                                label="Short Description"
                                value={carNewShortDesc}
                                onChange={e => setCarNewShortDesc(e.target.value.toUpperCase())}
                                color="blue"
                            />
                            <Input
                                type="text"
                                label="Long Description"
                                value={carNewLongDesc}
                                onChange={e => setCarNewLongDesc(e.target.value)}
                                color="blue"
                            />
                            <Input
                                type="text"
                                label="Sign (+/-)"
                                value={carNewSign}
                                onChange={e => setCarNewSign(e.target.value.slice(0, 1))}
                                maxLength={1}
                                color="blue"
                            />
                            <Input
                                type="text"
                                label="Driver Pay Type"
                                value={carNewDriverPayType}
                                onChange={e => setCarNewDriverPayType(e.target.value.toUpperCase())}
                                color="blue"
                            />
                            <Input
                                type="text"
                                label="Sub Type"
                                value={carNewSubType}
                                onChange={e => setCarNewSubType(e.target.value.toUpperCase())}
                                color="blue"
                            />
                            <Input
                                type="text"
                                label="Reversal Type"
                                value={carNewReversalType}
                                onChange={e => setCarNewReversalType(e.target.value.toUpperCase())}
                                color="blue"
                            />
                            <div className="lg:col-span-2 flex flex-wrap items-center gap-6">
                                <Checkbox
                                    label="Driver Post"
                                    checked={carNewDriverPost}
                                    onChange={e => setCarNewDriverPost(e.target.checked)}
                                    color="blue"
                                />
                                <Checkbox
                                    label="Default"
                                    checked={carNewDef}
                                    onChange={e => setCarNewDef(e.target.checked)}
                                    color="blue"
                                />
                                <Checkbox
                                    label="Notify"
                                    checked={carNewNotify}
                                    onChange={e => setCarNewNotify(e.target.checked)}
                                    color="blue"
                                />
                                <Checkbox
                                    label="Can Split"
                                    checked={carNewCanSplit}
                                    onChange={e => setCarNewCanSplit(e.target.checked)}
                                    color="blue"
                                />
                                <Button
                                    type="submit"
                                    color="blue"
                                    className="ml-auto flex items-center gap-2"
                                >
                                    <FaPlus /> Add Type
                                </Button>
                            </div>
                        </div>
                    </form>
                </CardBody>
            </Card>

            <Card className="shadow-lg border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max table-auto text-left">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">#</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Type</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Description</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Sign</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Driver Post</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Sub Type</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Reversal</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Splittable</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Settings</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Actions</Typography>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {carPaymentTypes && Array.isArray(carPaymentTypes) && carPaymentTypes.map((paymentType, index) => (
                                <tr key={paymentType.type} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <Typography variant="small" color="blue-gray" className="font-normal">{index + 1}</Typography>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex flex-col">
                                            <Typography variant="small" color="blue-gray" className="font-bold">{paymentType.type}</Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex flex-col">
                                            <Typography variant="small" color="blue-gray" className="font-bold">{paymentType.short_desc}</Typography>
                                            <Typography variant="small" color="gray" className="font-normal opacity-70">{paymentType.long_desc}</Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className={`px-2 py-1 rounded-md text-xs font-bold ${paymentType.sign === '+' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                            {paymentType.sign}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        {paymentType.driver_post ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <FaCheck className="text-green-500" />
                                                <span className="text-xs text-gray-500">{paymentType.driver_pay_type}</span>
                                            </div>
                                        ) : (
                                            <FaTimes className="text-gray-300 mx-auto" />
                                        )}
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-600">
                                            {paymentType.sub_type || '-'}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-600">
                                            {paymentType.reversal_type || '-'}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${paymentType.can_split === false ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                                            {paymentType.can_split === false ? 'No' : 'Yes'}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex justify-center gap-2">
                                            {paymentType.def && (
                                                <Tooltip content="Default">
                                                    <span className="p-1 bg-blue-50 text-blue-600 rounded"><FaCheck size={12} /></span>
                                                </Tooltip>
                                            )}
                                            {paymentType.notify && (
                                                <Tooltip content="Notifications Enabled">
                                                    <span className="p-1 bg-amber-50 text-amber-600 rounded"><FaBell size={12} /></span>
                                                </Tooltip>
                                            )}
                                            {paymentType.can_split !== false && (
                                                <Tooltip content="Split Enabled">
                                                    <span className="p-1 bg-green-50 text-green-700 rounded"><FaCut size={12} /></span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex justify-center gap-2">
                                            <Tooltip content="Edit">
                                                <Button size="sm" variant="text" color="blue-gray" className="p-2 rounded-full hover:bg-gray-100" onClick={() => openEditModal(paymentType, 'car')}>
                                                    <FaPenAlt />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip content="Delete">
                                                <Button size="sm" variant="text" color="red" className="p-2 rounded-full hover:bg-red-50" onClick={() => deleteCarPaymentType(paymentType.type)}>
                                                    <FaTrash />
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );

    const renderDriverPaymentTypes = () => (
        <>
            {/* Add New Driver Payment Type Form */}
            <Card className="mb-6 shadow-lg border border-gray-100">
                <CardBody className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-orange-50 rounded-lg">
                            <FaPlus className="text-orange-600" />
                        </div>
                        <Typography variant="h6" color="blue-gray">
                            Add New Driver Payment Type
                        </Typography>
                    </div>
                    <form onSubmit={createDriverPaymentType}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <Input
                                type="text"
                                label="Type Code"
                                value={driverNewType}
                                onChange={e => setDriverNewType(e.target.value.slice(0, 10).toUpperCase())}
                                maxLength={10}
                                color="orange"
                            />
                            <Input
                                type="text"
                                label="Short Description"
                                value={driverNewShortDesc}
                                onChange={e => setDriverNewShortDesc(e.target.value.toUpperCase())}
                                color="orange"
                            />
                            <Input
                                type="text"
                                label="Long Description"
                                value={driverNewLongDesc}
                                onChange={e => setDriverNewLongDesc(e.target.value)}
                                color="orange"
                            />
                            <Input
                                type="text"
                                label="Sign (+/-)"
                                value={driverNewSign}
                                onChange={e => setDriverNewSign(e.target.value.slice(0, 1))}
                                maxLength={1}
                                color="orange"
                            />
                            <Input
                                type="text"
                                label="Sub Type"
                                value={driverNewSubType}
                                onChange={e => setDriverNewSubType(e.target.value.toUpperCase())}
                                color="orange"
                            />
                            <Input
                                type="text"
                                label="Reversal Type"
                                value={driverNewReversalType}
                                onChange={e => setDriverNewReversalType(e.target.value.toUpperCase())}
                                color="orange"
                            />
                            {driverNewCarPost && (
                                <Input
                                    type="text"
                                    label="Car Post Type"
                                    value={driverNewCarPostType}
                                    onChange={e => setDriverNewCarPostType(e.target.value.toUpperCase())}
                                    color="orange"
                                />
                            )}
                            <div className="lg:col-span-2 flex flex-wrap items-center gap-6">
                                <Checkbox
                                    label="Car Post"
                                    checked={driverNewCarPost}
                                    onChange={e => setDriverNewCarPost(e.target.checked)}
                                    color="orange"
                                />
                                <Checkbox
                                    label="Default"
                                    checked={driverNewDef}
                                    onChange={e => setDriverNewDef(e.target.checked)}
                                    color="orange"
                                />
                                <Checkbox
                                    label="Notify"
                                    checked={driverNewNotify}
                                    onChange={e => setDriverNewNotify(e.target.checked)}
                                    color="orange"
                                />
                                <Button
                                    type="submit"
                                    color="orange"
                                    className="ml-auto flex items-center gap-2"
                                >
                                    <FaPlus /> Add Type
                                </Button>
                            </div>
                        </div>
                    </form>
                </CardBody>
            </Card>

            <Card className="shadow-lg border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max table-auto text-left">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">#</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Type</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Description</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Sign</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Car Post</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Sub Type</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Reversal</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Settings</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Actions</Typography>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {driverPaymentTypes && Array.isArray(driverPaymentTypes) && driverPaymentTypes.map((paymentType, index) => (
                                <tr key={paymentType.type} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <Typography variant="small" color="blue-gray" className="font-normal">{index + 1}</Typography>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex flex-col">
                                            <Typography variant="small" color="blue-gray" className="font-bold">{paymentType.type}</Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex flex-col">
                                            <Typography variant="small" color="blue-gray" className="font-bold">{paymentType.short_desc}</Typography>
                                            <Typography variant="small" color="gray" className="font-normal opacity-70">{paymentType.long_desc}</Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className={`px-2 py-1 rounded-md text-xs font-bold ${paymentType.sign === '+' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                            {paymentType.sign}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        {paymentType.car_post ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <FaCheck className="text-green-500" />
                                                <span className="text-xs text-gray-500">{paymentType.car_post_type}</span>
                                            </div>
                                        ) : (
                                            <FaTimes className="text-gray-300 mx-auto" />
                                        )}
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-600">
                                            {paymentType.sub_type || '-'}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-600">
                                            {paymentType.reversal_type || '-'}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex justify-center gap-2">
                                            {paymentType.def && (
                                                <Tooltip content="Default">
                                                    <span className="p-1 bg-blue-50 text-blue-600 rounded"><FaCheck size={12} /></span>
                                                </Tooltip>
                                            )}
                                            {paymentType.notify && (
                                                <Tooltip content="Notifications Enabled">
                                                    <span className="p-1 bg-amber-50 text-amber-600 rounded"><FaBell size={12} /></span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex justify-center gap-2">
                                            <Tooltip content="Edit">
                                                <Button size="sm" variant="text" color="blue-gray" className="p-2 rounded-full hover:bg-gray-100" onClick={() => openEditModal(paymentType, 'driver')}>
                                                    <FaPenAlt />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip content="Delete">
                                                <Button size="sm" variant="text" color="red" className="p-2 rounded-full hover:bg-red-50" onClick={() => deleteDriverPaymentType(paymentType.type)}>
                                                    <FaTrash />
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-3">Transaction Types</h1>
                    <div className="w-24 h-1.5 bg-[#FF5733] mx-auto rounded-full shadow-sm"></div>
                    <Typography className="mt-4 text-gray-600 font-medium">
                        Manage payment classifications for cars and drivers
                    </Typography>
                </div>
                
                {error && (
                    <div className='mb-6'>
                        <Alerty color='orange' message={error} setOpen={setError} />
                    </div>
                )}
                
                <div className="mb-8">
                    <div className="flex justify-center p-1 bg-white/50 backdrop-blur-sm rounded-xl shadow-sm w-fit mx-auto">
                        <button
                            type="button"
                            onClick={() => setActiveTab('Cars')}
                            className={`flex items-center gap-2 py-3 px-8 rounded-lg transition-all duration-300 font-semibold ${
                                activeTab === 'Cars'
                                    ? 'bg-white text-blue-600 shadow-md transform scale-105'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                            }`}
                        >
                            <FaCar className="text-lg" />
                            <span>Cars</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('Drivers')}
                            className={`flex items-center gap-2 py-3 px-8 rounded-lg transition-all duration-300 font-semibold ${
                                activeTab === 'Drivers'
                                    ? 'bg-white text-orange-600 shadow-md transform scale-105'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                            }`}
                        >
                            <FaUser className="text-lg" />
                            <span>Drivers</span>
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5733]"></div>
                    </div>
                ) : (
                    <div className="animate-fade-in-up">
                        {activeTab === 'Cars' && renderCarPaymentTypes()}
                        {activeTab === 'Drivers' && renderDriverPaymentTypes()}
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            <PaymentTypeEditModal
                isOpen={isEditModalOpen}
                onClose={closeEditModal}
                paymentType={editingPaymentType}
                type={editingType}
                onSave={handleSaveEdit}
                user={user}
                carPaymentTypes={carPaymentTypes}
                driverPaymentTypes={driverPaymentTypes}
            />
        </div>
    );
};

export default PaymentTypes; 